import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import MastersLayout from "@/components/masters/MastersLayout";
import TablePagination from "@/components/masters/TablePagination";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/modal";
import { Switch } from "@/components/ui/switch";
import { authenticatedApi, groupApi } from "@/services/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PlusCircle, Pencil, Trash2, Search, Loader2, MoreHorizontal, Eye } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Group type based on API response
type Group = {
  id: string;
  organizationId: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
};

type PaginatedResponse = {
  docs: Group[];
  totalDocs: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

const ITEMS_PER_PAGE = 10;

// Status options for the UI
const GROUP_STATUSES = ["Active", "Inactive"];

const GroupsPage = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    organizationId: string;
  }>({
    name: "",
    description: "",
    organizationId: "",
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { toast } = useToast();

  // Fetch organization ID from localStorage
  useEffect(() => {
    const storedOrg = localStorage.getItem("manterflow-user-profile");
    if (storedOrg) {
      try {
        const orgData = JSON.parse(storedOrg);
        setFormData((prev) => ({
          ...prev,
          organizationId: orgData.organizationId,
        }));
      } catch (e) {
        console.error("Error parsing organization data", e);
      }
    }
  }, []);

  // Fetch groups from API
  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await groupApi.getGroups({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        query: filters.query || undefined,
      });

      if (response && response.docs) {
        setGroups(response.docs);
        setTotalItems(response.totalDocs);
      }
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: (error as Error).message ?? "Failed to fetch groups",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch groups when page or filters change
  useEffect(() => {
    fetchGroups();
  }, [currentPage, filters.query]);

  const handleInputChange = (field: string, value: string) => {
    if (field === "name") {
      // Capitalize first letter of each word
      const capitalizedValue = (value as string)
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
      setFormData({ ...formData, [field]: capitalizedValue });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleEdit = (group: Group) => {
    setFormData({
      name: group.name.trim(),
      description: group.description.trim(),
      organizationId: group.organizationId,
    });
    setEditingId(group.id);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    setGroupToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!groupToDelete) return;

    try {
      setLoading(true);
      await groupApi.deleteGroup(groupToDelete);

      toast({
        title: "Group Deleted",
        description: "The group has been deleted successfully.",
      });

      // Refresh the groups list
      fetchGroups();
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: (error as Error).message ?? "Failed to delete group",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    }
  };

  const handleAdd = async () => {
    if (!formData.name.trim() || !formData.organizationId) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      // Only send required fields to the API
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        organizationId: formData.organizationId,
      };

      await groupApi.createGroup(payload);

      // Reset form data
      setFormData({
        name: "",
        description: "",
        organizationId: formData.organizationId, // Keep the organization ID
      });

      setShowForm(false);
      toast({
        title: "Success",
        description: "Group added successfully",
      });

      // Refresh the groups list
      fetchGroups();
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: (error as Error).message ?? "Failed to add group",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim() || !formData.organizationId || !editingId) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      // Only send required fields to the API
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        organizationId: formData.organizationId,
      };

      await groupApi.updateGroup(editingId, payload);

      // Reset form data
      setFormData({
        name: "",
        description: "",
        organizationId: formData.organizationId, // Keep the organization ID
      });

      setEditingId(null);
      setShowForm(false);
      toast({
        title: "Success",
        description: "Group updated successfully",
      });

      // Refresh the groups list
      fetchGroups();
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: (error as Error).message ?? "Failed to update group",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // No need for client-side filtering as the API handles search
  const currentItems = groups;

  // Calculate total pages based on total items from API
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  // Add this function to handle filter changes
  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Trim fields before submission
    const trimmedData = {
      ...formData,
      name: formData.name.trim(),
      description: formData.description.trim(),
    };

    if (!trimmedData.name || !trimmedData.organizationId) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    if (editingId) {
      handleUpdate();
    } else {
      handleAdd();
    }
  };

  return (
    <MastersLayout
    // title="Groups"
    // description="Manage system groups and their configurations"
    >
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search groups..."
                value={filters.query}
                onChange={(e) => handleFilterChange("query", e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <Button
              variant="outline"
              onClick={() => {
                setFilters({ query: "" });
                setCurrentPage(1);
              }}
            >
              Reset
            </Button>
          </div>
        </div>

        {/* <Card>
          <CardHeader> */}
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Groups</CardTitle>
                <CardDescription>
                  Manage system groups and their configurations
                </CardDescription>
              </div>
              <Button onClick={() => setShowForm(true)}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Group
              </Button>
            </div>
          {/* </CardHeader> */}
          {/* <CardContent> */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead style={{ minWidth: "200px" }}>
                    Description
                  </TableHead>
                  <TableHead className="w-[50px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentItems.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center py-4 text-muted-foreground"
                    >
                      {filters.query
                        ? "No groups match the search criteria"
                        : "No groups found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  currentItems.map((group) => (
                    <TableRow key={group.id}>
                      <TableCell className="font-medium whitespace-nowrap">
                        {group.name}
                      </TableCell>
                      <TableCell className="w-auto md:w-1/3 lg:w-2/5 overflow-hidden">
                        <div className="break-words whitespace-normal line-clamp-3">
                          {group.description}
                        </div>
                      </TableCell>
                      <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="cursor-pointer">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(group)} className="cursor-pointer">
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(group.id)} 
                          className="cursor-pointer text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {totalItems > ITEMS_PER_PAGE && (
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          {/* </CardContent>
        </Card> */}
      </div>

      {showForm && (
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>
                {editingId ? "Edit Group" : "Add New Group"}
              </DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Update the group information below"
                  : "Fill in the group information below"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter group name"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Enter group description"
                  rows={3}
                />
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    // Keep the organizationId when resetting the form
                    setFormData((prev) => ({
                      name: "",
                      description: "",
                      organizationId: prev.organizationId,
                    }));
                    setEditingId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingId ? "Update" : "Add"} Group
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure want to delete the group.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MastersLayout>
  );
};

export default GroupsPage;
